// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
'use client'

import { forwardRef } from 'react'

import { Button, ButtonProps } from '../ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

export type TooltipIconButtonProps = ButtonProps & {
    tooltip: string
    side?: 'top' | 'bottom' | 'left' | 'right'
}

export const TooltipIconButton = forwardRef<HTMLButtonElement, TooltipIconButtonProps>(
    ({ children, tooltip, side = 'top', className, style, size, ...rest }, ref) => {
        // Calculate styles based on size prop
        const getButtonSize = () => {
            if (size === 'lg') {
                return {
                    width: '32px',
                    height: '32px',
                    padding: '6px'
                }
            } else if (size === 'sm') {
                return {
                    width: '16px',
                    height: '16px',
                    padding: '1px'
                }
            } else {
                // Default size (md or icon)
                return {
                    width: '20px',
                    height: '20px',
                    padding: '2px'
                }
            }
        }

        const buttonSizeStyles = getButtonSize()

        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant='ghost'
                            {...rest}
                            style={{
                                ...buttonSizeStyles,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease', // eslint-disable-line string-to-lingui/missing-lingui-transformation
                                color: 'var(--foreground)',
                                ...style
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--accent)'
                                e.currentTarget.style.color = 'var(--accent-foreground)'
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = ''
                                e.currentTarget.style.color = 'var(--foreground)'
                            }}
                            ref={ref}
                        >
                            {children}
                            <span
                                style={{
                                    position: 'absolute',
                                    width: '1px',
                                    height: '1px',
                                    padding: 0,
                                    margin: '-1px',
                                    overflow: 'hidden',
                                    clip: 'rect(0, 0, 0, 0)',
                                    whiteSpace: 'nowrap',
                                    borderWidth: 0
                                }}
                            >
                                {tooltip}
                            </span>
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side={side} sideOffset={8}>
                        {tooltip}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        )
    }
)

TooltipIconButton.displayName = 'TooltipIconButton' // eslint-disable-line string-to-lingui/missing-lingui-transformation
